import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Edit, Save, X, AlertCircle, CheckCircle, RotateCw } from 'lucide-react';
import './shopfeemanager.css';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';

const GST_RATE = 0.05;

const ShopFeesManager = () => {
    const permissions = usePermissions('Shop Fees');
    const [shops, setShops] = useState([]);
    const [filteredShops, setFilteredShops] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editingShop, setEditingShop] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [platformFee, setPlatformFee] = useState(null);
    const [editing, setEditing] = useState(false);
    const [newFee, setNewFee] = useState('');
    const [shopFee, setShopFee] = useState(null);
    const [editingShopFee, setEditingShopFee] = useState(false);
    const [newShopFee, setNewShopFee] = useState('');
    const [baseDeliveryDistanceKm, setBaseDeliveryDistanceKm] = useState(null);
    const [newBaseDeliveryDistanceKm, setNewBaseDeliveryDistanceKm] = useState('');
    const [perKmDeliveryFee, setPerKmDeliveryFee] = useState(null);
    const [newPerKmDeliveryFee, setNewPerKmDeliveryFee] = useState('');
    const [maxDeliveryDistanceKm, setMaxDeliveryDistanceKm] = useState(null);
    const [newMaxDeliveryDistanceKm, setNewMaxDeliveryDistanceKm] = useState('');
    const [shippingCharges, setShippingCharges] = useState(null);
    const [newShippingCharges, setNewShippingCharges] = useState('');
    const [freeDeliveryMinOrderValue2, setFreeDeliveryMinOrderValue2] = useState(null);
    const [newFreeDeliveryMinOrderValue2, setNewFreeDeliveryMinOrderValue2] = useState('');
    const [paymentGatewayCharges, setPaymentGatewayCharges] = useState(null);
    const [editingPaymentGatewayCharges, setEditingPaymentGatewayCharges] = useState(false);
    const [newPaymentGatewayCharges, setNewPaymentGatewayCharges] = useState('');
    const [globalCommission, setGlobalCommission] = useState(null);
    const [editingGlobalCommission, setEditingGlobalCommission] = useState(false);
    const [newGlobalCommission, setNewGlobalCommission] = useState('');

    // Filter states
    const [filters, setFilters] = useState({
        minBaseFee: '',
        maxBaseFee: '',
        minPerKmFee: '',
        maxPerKmFee: '',
        minDistance: '',
        maxDistance: '',
        searchTerm: ''
    });

    // Form states
    const [formData, setFormData] = useState({
        baseDeliveryFee: '',
        baseDeliveryDistanceKm: '',
        perKmDeliveryFee: '',
        maxDeliveryDistanceKm: '',
        freeDeliveryMinOrderValue: '',
        freeDeliveryMinOrderValue2: '',
        paymentGateWayCharges: '',
        commission: '',
        shippingCharges: '',
        deliveryFrom: 'SELF',
    });

    const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/admin-panel/shop-fee`;

    // Fetch all shops
    const fetchShops = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiWithAuth.get(`${API_BASE}/all`);
            
            if (response.data.success) {
                setShops(response.data.data);
                setFilteredShops(response.data.data);
            } else {
                setError('Failed to fetch shops');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    // Check if filters are active
    const hasActiveFilters = useCallback(() => {
        return Object.values(filters).some(value => value !== '');
    }, [filters]);

    // Apply filters
    const applyFilters = useCallback(async () => {
        if (!hasActiveFilters()) {
            setFilteredShops(shops);
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value && key !== 'searchTerm') {
                    queryParams.append(key, value);
                }
            });

            const response = await apiWithAuth.get(`${API_BASE}/by-fee-range?${queryParams}`);

            if (response.data.success) {
                let filtered = response.data.data;

                // Apply search term filter
                if (filters.searchTerm) {
                    filtered = filtered.filter(shop =>
                        shop.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                        shop.owner.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                        shop.ownerPhone.includes(filters.searchTerm)
                    );
                }

                setFilteredShops(filtered);
            } else {
                setError('Failed to filter shops');
            }
        } catch (err) {
            setError('Error applying filters');
        } finally {
            setLoading(false);
        }
    }, [filters, shops, API_BASE, hasActiveFilters]);

    // Clear filters
    const clearFilters = () => {
        setFilters({
            minBaseFee: '',
            maxBaseFee: '',
            minPerKmFee: '',
            maxPerKmFee: '',
            minDistance: '',
            maxDistance: '',
            searchTerm: ''
        });
        setFilteredShops(shops);
    };

    // Update shop fees
    const updateShopFees = async (shopId, fees) => {
        setLoading(true);
        setError('');
        try {
            const response = await apiWithAuth.post(`${API_BASE}/${shopId}/fees`, fees);

            if (response.data.success) {
                setSuccess('Shop fees updated successfully');
                setEditingShop(null);
                // Update local rows immediately so UI reflects saved values,
                // even if the backend response omits newer fields.
                const updated = (prev) =>
                    prev.map((s) => (s.id === shopId ? { ...s, ...fees } : s));
                setShops(updated);
                setFilteredShops(updated);
            } else {
                setError(response.data.error || 'Failed to update shop fees');
            }
        } catch (err) {
            setError('Error updating shop fees');
        } finally {
            setLoading(false);
        }
    };

    // Reset shop fees
    const resetShopFees = async (shopId) => {
        if (!window.confirm('Are you sure you want to reset all delivery fees to default values?')) {
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await apiWithAuth.delete(`${API_BASE}/${shopId}/fees`);

            if (response.data.success) {
                setSuccess('Shop fees reset successfully');
                fetchShops();
            } else {
                setError(response.data.error || 'Failed to reset shop fees');
            }
        } catch (err) {
            setError('Error resetting shop fees');
        } finally {
            setLoading(false);
        }
    };

    // Handle edit
    const handleEdit = (shop) => {
        setEditingShop(shop.id);
        setFormData({
            baseDeliveryFee: shop.baseDeliveryFee || '',
            baseDeliveryDistanceKm: shop.baseDeliveryDistanceKm || '',
            perKmDeliveryFee: shop.perKmDeliveryFee || '',
            maxDeliveryDistanceKm: shop.maxDeliveryDistanceKm || '',
            freeDeliveryMinOrderValue: shop.freeDeliveryMinOrderValue || '',
            freeDeliveryMinOrderValue2: shop.freeDeliveryMinOrderValue2 ?? '',
            paymentGateWayCharges: shop.paymentGateWayCharges || '',
            commission: shop.commission || '',
            shippingCharges: shop.shippingCharges ?? '',
            deliveryFrom: shop.deliveryFrom || 'SELF',
        });
    };

    // Handle save
    const handleSave = () => {
        const fees = {};
        if (formData.baseDeliveryFee !== '') fees.baseDeliveryFee = parseFloat(formData.baseDeliveryFee);
        if (formData.baseDeliveryDistanceKm !== '') fees.baseDeliveryDistanceKm = parseFloat(formData.baseDeliveryDistanceKm);
        if (formData.perKmDeliveryFee !== '') fees.perKmDeliveryFee = parseFloat(formData.perKmDeliveryFee);
        if (formData.maxDeliveryDistanceKm !== '') fees.maxDeliveryDistanceKm = parseFloat(formData.maxDeliveryDistanceKm);

        const free1 =
            formData.freeDeliveryMinOrderValue === '' ? null : parseFloat(formData.freeDeliveryMinOrderValue);
        const free2 =
            formData.freeDeliveryMinOrderValue2 === '' ? null : parseFloat(formData.freeDeliveryMinOrderValue2);

        // Some backends still persist only `freeDeliveryMinOrderValue`.
        // If user filled only the "FREE delivery" field, mirror it into `freeDeliveryMinOrderValue`
        // so the saved value shows up after refresh.
        fees.freeDeliveryMinOrderValue = free1 ?? free2;
        fees.freeDeliveryMinOrderValue2 = free2 ?? free1;

        if (formData.paymentGateWayCharges !== '') fees.paymentGateWayCharges = parseFloat(formData.paymentGateWayCharges);
        if (formData.commission !== '') fees.commission = parseFloat(formData.commission);
        if (formData.shippingCharges !== '') fees.shippingCharges = parseFloat(formData.shippingCharges);
        fees.deliveryFrom = formData.deliveryFrom || 'SELF';

        updateShopFees(editingShop, fees);
    };

    // Handle cancel
    const handleCancel = () => {
        setEditingShop(null);
        setFormData({
            baseDeliveryFee: '',
            baseDeliveryDistanceKm: '',
            perKmDeliveryFee: '',
            maxDeliveryDistanceKm: '',
            freeDeliveryMinOrderValue: '',
            freeDeliveryMinOrderValue2: '',
            paymentGateWayCharges: '',
            commission: '',
            shippingCharges: '',
            deliveryFrom: 'SELF',
        });
    };

    useEffect(() => {
        fetchShops();
    }, [fetchShops]);

    useEffect(() => {
        applyFilters();
    }, [filters, shops, applyFilters]);

    // Auto-hide messages
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    // Fetch platform fee on mount
    useEffect(() => {
        const fetchPlatformFee = async () => {
            try {
                const res = await apiWithAuth.get('/admin-panel/platform-fee');
                setPlatformFee(res.data.platformFee);
                setNewFee(res.data.platformFee);
            } catch (error) {
                console.error('Failed to fetch platform fee:', error);
            }
        };

        fetchPlatformFee();
    }, []);

    const handlePlatformFeeSave = async () => {
        try {
            const res = await apiWithAuth.put('/admin-panel/platform-fee', {
                platformFee: Number(newFee),
            });
            setPlatformFee(res.data.platformFee);
            setEditing(false);
        } catch (error) {
            console.error('Failed to update platform fee:', error);
        }
    };

    useEffect(() => {
        const fetchShopFee = async () => {
            try {
                const res = await apiWithAuth.get('/admin-panel/shop-fee/base');
                const payload = res.data?.data ?? res.data ?? {};
                const nextShopFee = payload.shopFee ?? payload.baseDeliveryFee ?? null;

                setShopFee(nextShopFee);
                setNewShopFee(nextShopFee ?? '');

                const nextBaseDeliveryDistanceKm = payload.baseDeliveryDistanceKm ?? null;
                setBaseDeliveryDistanceKm(nextBaseDeliveryDistanceKm);
                setNewBaseDeliveryDistanceKm(nextBaseDeliveryDistanceKm ?? '');

                const nextPerKmDeliveryFee = payload.perKmDeliveryFee ?? null;
                setPerKmDeliveryFee(nextPerKmDeliveryFee);
                setNewPerKmDeliveryFee(nextPerKmDeliveryFee ?? '');

                const nextMaxDeliveryDistanceKm = payload.maxDeliveryDistanceKm ?? null;
                setMaxDeliveryDistanceKm(nextMaxDeliveryDistanceKm);
                setNewMaxDeliveryDistanceKm(nextMaxDeliveryDistanceKm ?? '');

                const nextShippingCharges = payload.shippingCharges ?? null;
                setShippingCharges(nextShippingCharges);
                setNewShippingCharges(nextShippingCharges ?? '');

                const nextFreeDeliveryMinOrderValue2 = payload.freeDeliveryMinOrderValue2 ?? null;
                setFreeDeliveryMinOrderValue2(nextFreeDeliveryMinOrderValue2);
                setNewFreeDeliveryMinOrderValue2(nextFreeDeliveryMinOrderValue2 ?? '');
            } catch (error) {
                console.error('Failed to fetch shop fee:', error);
            }
        };

        fetchShopFee();
    }, []);

    const handleShopFeeSave = async () => {
        try {
            const res = await apiWithAuth.put('/admin-panel/shop-fee/base', {
                shopFee: Number(newShopFee),
                baseDeliveryDistanceKm: newBaseDeliveryDistanceKm === '' ? null : Number(newBaseDeliveryDistanceKm),
                perKmDeliveryFee: newPerKmDeliveryFee === '' ? null : Number(newPerKmDeliveryFee),
                maxDeliveryDistanceKm: newMaxDeliveryDistanceKm === '' ? null : Number(newMaxDeliveryDistanceKm),
                shippingCharges: newShippingCharges === '' ? null : Number(newShippingCharges),
                freeDeliveryMinOrderValue2: newFreeDeliveryMinOrderValue2 === '' ? null : Number(newFreeDeliveryMinOrderValue2),
            });
            const payload = res.data?.data ?? res.data ?? {};
            const nextShopFee = payload.shopFee ?? payload.baseDeliveryFee ?? Number(newShopFee);
            setShopFee(nextShopFee);

            const nextBaseDeliveryDistanceKm = payload.baseDeliveryDistanceKm ?? (newBaseDeliveryDistanceKm === '' ? null : Number(newBaseDeliveryDistanceKm));
            setBaseDeliveryDistanceKm(nextBaseDeliveryDistanceKm);

            const nextPerKmDeliveryFee = payload.perKmDeliveryFee ?? (newPerKmDeliveryFee === '' ? null : Number(newPerKmDeliveryFee));
            setPerKmDeliveryFee(nextPerKmDeliveryFee);

            const nextMaxDeliveryDistanceKm = payload.maxDeliveryDistanceKm ?? (newMaxDeliveryDistanceKm === '' ? null : Number(newMaxDeliveryDistanceKm));
            setMaxDeliveryDistanceKm(nextMaxDeliveryDistanceKm);

            const nextShippingCharges = payload.shippingCharges ?? (newShippingCharges === '' ? null : Number(newShippingCharges));
            setShippingCharges(nextShippingCharges);

            const nextFreeDeliveryMinOrderValue2 = payload.freeDeliveryMinOrderValue2 ?? (newFreeDeliveryMinOrderValue2 === '' ? null : Number(newFreeDeliveryMinOrderValue2));
            setFreeDeliveryMinOrderValue2(nextFreeDeliveryMinOrderValue2);

            setEditingShopFee(false);
        } catch (error) {
            console.error('Failed to update shop fee:', error);
        }
    };

    // Fetch payment gateway charges on mount
    useEffect(() => {
        const fetchPaymentGatewayCharges = async () => {
            try {
                const res = await apiWithAuth.get('/admin-panel/shop-fee/global/payment-gateway-charges');
                const charges = res.data.data?.chargePercentage || 0;
                setPaymentGatewayCharges(charges);
                setNewPaymentGatewayCharges(charges);
            } catch (error) {
                console.error('Failed to fetch payment gateway charges:', error);
                setPaymentGatewayCharges(0);
                setNewPaymentGatewayCharges(0);
            }
        };

        fetchPaymentGatewayCharges();
    }, []);

    const handlePaymentGatewayChargesSave = async () => {
        try {
            const res = await apiWithAuth.put('/admin-panel/shop-fee/global/payment-gateway-charges', {
                paymentGatewayCharges: Number(newPaymentGatewayCharges),
            });
            const charges = res.data.data?.chargePercentage || Number(newPaymentGatewayCharges);
            setPaymentGatewayCharges(charges);
            setEditingPaymentGatewayCharges(false);
        } catch (error) {
            console.error('Failed to update payment gateway charges:', error);
        }
    };

    // Fetch global commission on mount
    useEffect(() => {
        const fetchGlobalCommission = async () => {
            try {
                const res = await apiWithAuth.get('/admin-panel/shop-fee/global/commission');
                const commission = res.data.data?.commissionPercentage || 0;
                setGlobalCommission(commission);
                setNewGlobalCommission(commission);
            } catch (error) {
                console.error('Failed to fetch global commission:', error);
                setGlobalCommission(0);
                setNewGlobalCommission(0);
            }
        };

        fetchGlobalCommission();
    }, []);

    const handleGlobalCommissionSave = async () => {
        try {
            const res = await apiWithAuth.put('/admin-panel/shop-fee/global/commission', {
                commission: Number(newGlobalCommission),
            });
            const commission = res.data.data?.commissionPercentage || Number(newGlobalCommission);
            setGlobalCommission(commission);
            setEditingGlobalCommission(false);
        } catch (error) {
            console.error('Failed to update global commission:', error);
        }
    };

    return (
        <div className="shopFee-container">

            <div className="shopFee-header">
                <div className="shopFee-headerTop">
                    <div className="shopFee-headerLeft">
                        <h1 className="shopFee-title">Delivery Fees Manager</h1>
                        <p className="shopFee-subtitle">Manage delivery fees, charges, and commissions for all shops</p>
                    </div>

                    <div className="shopFee-headerRight">
                        <h1 className="shopFee-title">KDP Invoice Fees Manager</h1>
                        <div className="shopFee-invoiceFeesList">
                            {paymentGatewayCharges !== null && (
                                <div className="shopFee-platform-fee">
                                    <strong>Payment Gateway Charges (Globally):</strong>{' '}
                                    {editingPaymentGatewayCharges ? (
                                        <>
                                            <input
                                                type="number"
                                                value={newPaymentGatewayCharges}
                                                onChange={(e) => setNewPaymentGatewayCharges(e.target.value)}
                                                className="shopFee-inlineNumberInput"
                                                step="0.01"
                                            />
                                            <span>%</span>
                                            <button onClick={handlePaymentGatewayChargesSave}>Save</button>
                                            <button
                                                onClick={() => {
                                                    setEditingPaymentGatewayCharges(false);
                                                    setNewPaymentGatewayCharges(paymentGatewayCharges);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {paymentGatewayCharges || '0'}%{' '}
                                            <button onClick={() => setEditingPaymentGatewayCharges(true)}>Edit</button>
                                        </>
                                    )}
                                </div>
                            )}

                            {globalCommission !== null && (
                                <div className="shopFee-platform-fee">
                                    <strong>Global Commission:</strong>{' '}
                                    {editingGlobalCommission ? (
                                        <>
                                            <input
                                                type="number"
                                                value={newGlobalCommission}
                                                onChange={(e) => setNewGlobalCommission(e.target.value)}
                                                className="shopFee-inlineNumberInput"
                                                step="0.01"
                                            />
                                            <span>%</span>
                                            <button onClick={handleGlobalCommissionSave}>Save</button>
                                            <button
                                                onClick={() => {
                                                    setEditingGlobalCommission(false);
                                                    setNewGlobalCommission(globalCommission);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {globalCommission || '0'}%{' '}
                                            <button onClick={() => setEditingGlobalCommission(true)}>Edit</button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="shopFee-global-fees">
                    {platformFee !== null && (
                        <div className="shopFee-platform-fee">
                            <strong>Platform Fee (Globally):</strong>{' '}
                            {editing ? (
                                <>
                                    <input
                                        type="number"
                                        value={newFee}
                                        onChange={(e) => setNewFee(e.target.value)}
                                        style={{ width: '80px', marginRight: '8px' }}
                                    />
                                    <span>
                                        + 18% GST = ₹
                                        {newFee
                                            ? (Number(newFee) * 1.18).toFixed(2)
                                            : '0.00'}
                                    </span>
                                    <button onClick={handlePlatformFeeSave}>Save</button>
                                    <button
                                        onClick={() => {
                                            setEditing(false);
                                            setNewFee(platformFee);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <>
                                    ₹{platformFee} + 18% GST = ₹{(platformFee * 1.18).toFixed(2)}{' '}
                                    <button onClick={() => setEditing(true)}>Edit</button>
                                </>
                            )}
                        </div>
                    )}

                    {shopFee !== null && (
                        <div className="shopFee-platform-fee">
                            <strong>Delivery Fee (Globally - default):</strong>{' '}
                            {editingShopFee ? (
                                <>
                                    <div className="shopFee-globalDeliveryGrid">
                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">BASE FEE (₹)</div>
                                            <input
                                                type="number"
                                                value={newShopFee}
                                                onChange={(e) => setNewShopFee(e.target.value)}
                                                className="shopFee-globalDeliveryInput"
                                                step="0.01"
                                            />
                                            <div className="shopFee-globalDeliveryHelp">
                                                With GST: ₹{newShopFee ? (Number(newShopFee) * 1.05).toFixed(2) : '0.00'}
                                            </div>
                                            <input
                                                type="number"
                                                value={newBaseDeliveryDistanceKm}
                                                onChange={(e) => setNewBaseDeliveryDistanceKm(e.target.value)}
                                                className="shopFee-globalDeliveryInput"
                                                step="0.01"
                                            />
                                            <div className="shopFee-globalDeliveryHelp">
                                                Base Fee covers up to {newBaseDeliveryDistanceKm || 0} km
                                            </div>
                                        </div>

                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">PER KM FEE (₹)</div>
                                            <input
                                                type="number"
                                                value={newPerKmDeliveryFee}
                                                onChange={(e) => setNewPerKmDeliveryFee(e.target.value)}
                                                className="shopFee-globalDeliveryInput"
                                                step="0.01"
                                            />
                                            <div className="shopFee-globalDeliveryHelp">
                                                With GST: ₹{newPerKmDeliveryFee ? (Number(newPerKmDeliveryFee) * 1.05).toFixed(2) : '0.00'}
                                            </div>
                                            <div className="shopFee-globalDeliveryHelp">
                                                Per km after {newBaseDeliveryDistanceKm || 0} km
                                            </div>
                                        </div>

                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">MAX DELIVERY DISTANCE (KM)</div>
                                            <input
                                                type="number"
                                                value={newMaxDeliveryDistanceKm}
                                                onChange={(e) => setNewMaxDeliveryDistanceKm(e.target.value)}
                                                className="shopFee-globalDeliveryInput"
                                                step="0.01"
                                            />
                                            <div className="shopFee-globalDeliveryHelp">Shipping charges Km range</div>
                                        </div>

                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">SHIPPING CHARGES (₹)</div>
                                            <input
                                                type="number"
                                                value={newShippingCharges}
                                                onChange={(e) => setNewShippingCharges(e.target.value)}
                                                className="shopFee-globalDeliveryInput"
                                                step="0.01"
                                            />
                                            <div className="shopFee-globalDeliveryHelp">Shipping charges</div>
                                        </div>
                                    </div>
                                    <button onClick={handleShopFeeSave}>Save</button>
                                    <button
                                        onClick={() => {
                                            setEditingShopFee(false);
                                            setNewShopFee(shopFee);
                                            setNewBaseDeliveryDistanceKm(baseDeliveryDistanceKm ?? '');
                                            setNewPerKmDeliveryFee(perKmDeliveryFee ?? '');
                                            setNewMaxDeliveryDistanceKm(maxDeliveryDistanceKm ?? '');
                                            setNewShippingCharges(shippingCharges ?? '');
                                            setNewFreeDeliveryMinOrderValue2(freeDeliveryMinOrderValue2 ?? '');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => setEditingShopFee(true)}>Edit</button>
                                    <div className="shopFee-globalDeliveryGrid">
                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">BASE FEE (₹)</div>
                                            <div className="shopFee-globalDeliveryValue">{shopFee ?? '—'}</div>
                                            <div className="shopFee-globalDeliveryHelp">
                                                With GST: ₹{shopFee ? (Number(shopFee) * 1.05).toFixed(2) : '0.00'}
                                            </div>
                                            <div className="shopFee-globalDeliveryValue">{baseDeliveryDistanceKm ?? '—'}</div>
                                            <div className="shopFee-globalDeliveryHelp">
                                                Base Fee covers up to {baseDeliveryDistanceKm || 0} km
                                            </div>
                                        </div>

                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">PER KM FEE (₹)</div>
                                            <div className="shopFee-globalDeliveryValue">{perKmDeliveryFee ?? '—'}</div>
                                            <div className="shopFee-globalDeliveryHelp">
                                                With GST: ₹{perKmDeliveryFee ? (Number(perKmDeliveryFee) * 1.05).toFixed(2) : '0.00'}
                                            </div>
                                            <div className="shopFee-globalDeliveryHelp">
                                                Per km after {baseDeliveryDistanceKm || 0} km
                                            </div>
                                        </div>

                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">MAX DELIVERY DISTANCE (KM)</div>
                                            <div className="shopFee-globalDeliveryValue">{maxDeliveryDistanceKm ?? '—'}</div>
                                            <div className="shopFee-globalDeliveryHelp">Shipping charges Km range</div>
                                        </div>

                                        <div className="shopFee-globalDeliveryCol">
                                            <div className="shopFee-globalDeliveryLabel">SHIPPING CHARGES (₹)</div>
                                            <div className="shopFee-globalDeliveryValue">{shippingCharges ?? '—'}</div>
                                            <div className="shopFee-globalDeliveryHelp">Shipping charges</div>
                                        </div>
                                    </div>

                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <div className="shopFee-alert shopFee-alert-error">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            {success && (
                <div className="shopFee-alert shopFee-alert-success">
                    <CheckCircle size={16} />
                    {success}
                </div>
            )}

            <div className="shopFee-controls">
                <div className="shopFee-search-bar">
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
                        <input
                            type="text"
                            placeholder="Search shops or owners..."
                            className="shopFee-search-input"
                            style={{ paddingLeft: '36px' }}
                            value={filters.searchTerm}
                            onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                        />
                    </div>
                    <button
                        className="shopFee-btn shopFee-btn-outline"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter size={16} />
                        Filters
                    </button>
                    {hasActiveFilters() && (
                        <button
                            className="shopFee-btn shopFee-btn-secondary"
                            onClick={clearFilters}
                        >
                            Clear
                        </button>
                    )}
                </div>

                {showFilters && (
                    <div className="shopFee-filters">
                        <div className="shopFee-filter-group">
                            <label className="shopFee-filter-label">Min Base Fee</label>
                            <input
                                type="number"
                                className="shopFee-filter-input"
                                placeholder="0"
                                value={filters.minBaseFee}
                                onChange={(e) => setFilters(prev => ({ ...prev, minBaseFee: e.target.value }))}
                            />
                        </div>
                        <div className="shopFee-filter-group">
                            <label className="shopFee-filter-label">Max Base Fee</label>
                            <input
                                type="number"
                                className="shopFee-filter-input"
                                placeholder="100"
                                value={filters.maxBaseFee}
                                onChange={(e) => setFilters(prev => ({ ...prev, maxBaseFee: e.target.value }))}
                            />
                        </div>
                        <div className="shopFee-filter-group">
                            <label className="shopFee-filter-label">Min Per KM Fee</label>
                            <input
                                type="number"
                                className="shopFee-filter-input"
                                placeholder="0"
                                value={filters.minPerKmFee}
                                onChange={(e) => setFilters(prev => ({ ...prev, minPerKmFee: e.target.value }))}
                            />
                        </div>
                        <div className="shopFee-filter-group">
                            <label className="shopFee-filter-label">Max Per KM Fee</label>
                            <input
                                type="number"
                                className="shopFee-filter-input"
                                placeholder="50"
                                value={filters.maxPerKmFee}
                                onChange={(e) => setFilters(prev => ({ ...prev, maxPerKmFee: e.target.value }))}
                            />
                        </div>
                        <div className="shopFee-filter-group">
                            <label className="shopFee-filter-label">Min Distance</label>
                            <input
                                type="number"
                                className="shopFee-filter-input"
                                placeholder="0"
                                value={filters.minDistance}
                                onChange={(e) => setFilters(prev => ({ ...prev, minDistance: e.target.value }))}
                            />
                        </div>
                        <div className="shopFee-filter-group">
                            <label className="shopFee-filter-label">Max Distance</label>
                            <input
                                type="number"
                                className="shopFee-filter-input"
                                placeholder="100"
                                value={filters.maxDistance}
                                onChange={(e) => setFilters(prev => ({ ...prev, maxDistance: e.target.value }))}
                            />
                        </div>
                    </div>
                )}

                <div className="shopFee-summary" style={{ marginTop: '1rem', fontSize: '0.95rem', color: '#444' }}>
                    <p><strong>Total Shops Found:</strong> {filteredShops.length}</p>
                    <p><strong><em>Notes:</em></strong></p>
                    <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', listStyle: 'disc', listStylePosition: 'outside' }}>
                        <li>All delivery charges shown include an additional <strong>5% GST</strong> as per government regulations.</li>
                        <li>Platform fee with <strong>18% GST</strong> will be applied separately on all orders.</li>
                        <li>If a shop hasn't set custom delivery fees, the global shop base fee will be used as default.</li>
                        <li><strong>Free Delivery:</strong> When enabled, customers get free delivery if their order total meets or exceeds the minimum value.</li>
                        <li>Leave the free delivery field empty to disable the free delivery offer for that shop.</li>
                    </ul>
                </div>
            </div>

            <div className="shopFee-table-container">
                {loading ? (
                    <div className="shopFee-loading">
                        <div>Loading shops...</div>
                    </div>
                ) : filteredShops.length === 0 ? (
                    <div className="shopFee-empty">
                        <div className="shopFee-empty-title">No shops found</div>
                        <div>Try adjusting your filters or search terms</div>
                    </div>
                ) : (
                    <table className="shopFee-table">
                        <thead className="shopFee-table-header">
                            <tr>
                                <th>Shop Details</th>
                                <th>Base Fee (₹)</th>
                                <th>Per KM Fee (₹)</th>
                                <th>Max Distance (KM)</th>
                                <th>Shipping Charges (₹)</th>
                                <th>Delivery From</th>
                                <th>Min Order Value (₹)</th>
                                <th>Min order value for FREE delivery</th>
                                <th>Payment Gateway Charges (%)</th>
                                <th>Commission (%)</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredShops.map((shop) => (
                                <tr key={shop.id} className="shopFee-table-row">
                                    <td className="shopFee-table-cell">
                                        <div className="shopFee-shop-name">{shop.name}</div>
                                        <div className="shopFee-shop-owner">{shop.owner} • {shop.ownerPhone}</div>
                                        <div className="shopFee-shop-address">{shop.shopAddress}</div>
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <>
                                                <input
                                                    type="number"
                                                    className="shopFee-fee-input"
                                                    value={formData.baseDeliveryFee}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            baseDeliveryFee: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="0"
                                                />
                                                <div className="shopFee-gst-note">
                                                    With GST: ₹{(parseFloat(formData.baseDeliveryFee || 0) * (1 + GST_RATE)).toFixed(2)}
                                                </div>

                                                <input
                                                    type="number"
                                                    className="shopFee-fee-input"
                                                    style={{ marginTop: '6px' }}
                                                    value={formData.baseDeliveryDistanceKm}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            baseDeliveryDistanceKm: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="0"
                                                />
                                                <div className="shopFee-gst-note">
                                                    Base Fee covers up to {formData.baseDeliveryDistanceKm || 0} km
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="shopFee-fee-value">
                                                    ₹{shop.baseDeliveryFee} (₹{(shop.baseDeliveryFee * (1 + GST_RATE)).toFixed(2)} incl. GST)
                                                </span>
                                                <div className="shopFee-distance-info">
                                                    Up to {shop.baseDeliveryDistanceKm || 0} km
                                                </div>
                                            </>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <>
                                                <input
                                                    type="number"
                                                    className="shopFee-fee-input"
                                                    value={formData.perKmDeliveryFee}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, perKmDeliveryFee: e.target.value }))}
                                                    placeholder="0"
                                                />
                                                <div className="shopFee-gst-note">
                                                    With GST: ₹{(parseFloat(formData.perKmDeliveryFee || 0) * (1 + GST_RATE)).toFixed(2)}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="shopFee-fee-value">
                                                    ₹{shop.perKmDeliveryFee} (₹{(shop.perKmDeliveryFee * (1 + GST_RATE)).toFixed(2)} incl. GST)
                                                </span>
                                                <div className="shopFee-distance-info">
                                                    ₹{(shop.perKmDeliveryFee * (1 + GST_RATE)).toFixed(2)} per km after {shop.baseDeliveryDistanceKm || 0} km
                                                </div>
                                            </>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <input
                                                type="number"
                                                className="shopFee-fee-input"
                                                value={formData.maxDeliveryDistanceKm}
                                                onChange={(e) => setFormData(prev => ({ ...prev, maxDeliveryDistanceKm: e.target.value }))}
                                                placeholder="0"
                                            />
                                        ) : (
                                            <span className="shopFee-fee-value">{shop.maxDeliveryDistanceKm || 'N/A'}</span>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <input
                                                type="number"
                                                className="shopFee-fee-input"
                                                value={formData.shippingCharges}
                                                onChange={(e) => setFormData(prev => ({ ...prev, shippingCharges: e.target.value }))}
                                                placeholder="0"
                                                step="0.01"
                                            />
                                        ) : (
                                            <span className="shopFee-fee-value">
                                                {shop.shippingCharges != null && shop.shippingCharges !== '' ? `₹${shop.shippingCharges}` : '—'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {(() => {
                                            const isKD = (editingShop === shop.id ? formData.deliveryFrom : shop.deliveryFrom) === 'KD';
                                            const isDisabled = editingShop !== shop.id;
                                            return (
                                                <div className="shopFee-deliveryFrom-toggle-wrap">
                                                    <button
                                                        type="button"
                                                        className={`shopFee-delivery-toggle-switch ${
                                                            isKD ? 'enabled' : 'disabled'
                                                        } ${isDisabled ? 'is-disabled' : ''}`}
                                                        disabled={isDisabled}
                                                        onClick={() => {
                                                            if (isDisabled) return;
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                deliveryFrom: prev.deliveryFrom === 'KD' ? 'SELF' : 'KD',
                                                            }));
                                                        }}
                                                        aria-pressed={isKD}
                                                    >
                                                        <span className="shopFee-delivery-toggle-knob" />
                                                    </button>
                                                    <span className="shopFee-delivery-toggle-label">
                                                        {isKD ? 'KD' : 'SELF'}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <>
                                                <input
                                                    type="number"
                                                    className="shopFee-fee-input"
                                                    value={formData.freeDeliveryMinOrderValue}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, freeDeliveryMinOrderValue: e.target.value }))}
                                                    placeholder="Min order for free delivery"
                                                />
                                                <div className="shopFee-gst-note">
                                                    {formData.freeDeliveryMinOrderValue ?
                                                        `Free delivery on orders ≥ ₹${formData.freeDeliveryMinOrderValue}` :
                                                        'No free delivery offer'
                                                    }
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {shop.freeDeliveryMinOrderValue ? (
                                                    <>
                                                        <span className="shopFee-fee-value">₹{shop.freeDeliveryMinOrderValue}</span>
                                                        <div className="shopFee-distance-info">
                                                           Order will palace when amount is  ≥ ₹{shop.freeDeliveryMinOrderValue}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="shopFee-fee-value" style={{ color: '#6b7280' }}>Not enabled</span>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <>
                                                <input
                                                    type="number"
                                                    className="shopFee-fee-input"
                                                    value={formData.freeDeliveryMinOrderValue2}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, freeDeliveryMinOrderValue2: e.target.value }))}
                                                    placeholder="Min order for FREE delivery"
                                                />
                                                <div className="shopFee-gst-note">
                                                    {formData.freeDeliveryMinOrderValue2
                                                        ? `FREE delivery on orders ≥ ₹${formData.freeDeliveryMinOrderValue2}`
                                                        : 'Not enabled'}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {shop.freeDeliveryMinOrderValue2 != null && shop.freeDeliveryMinOrderValue2 !== '' ? (
                                                    <>
                                                        <span className="shopFee-fee-value">₹{shop.freeDeliveryMinOrderValue2}</span>
                                                        <div className="shopFee-distance-info">
                                                            FREE delivery on orders ≥ ₹{shop.freeDeliveryMinOrderValue2}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="shopFee-fee-value" style={{ color: '#6b7280' }}>Not enabled</span>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <input
                                                type="number"
                                                className="shopFee-fee-input"
                                                value={formData.paymentGateWayCharges}
                                                onChange={(e) => setFormData(prev => ({ ...prev, paymentGateWayCharges: e.target.value }))}
                                                placeholder="0"
                                                step="0.01"
                                            />
                                        ) : (
                                            <span className="shopFee-fee-value">{shop.paymentGateWayCharges || '0'}%</span>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        {editingShop === shop.id ? (
                                            <input
                                                type="number"
                                                className="shopFee-fee-input"
                                                value={formData.commission}
                                                onChange={(e) => setFormData(prev => ({ ...prev, commission: e.target.value }))}
                                                placeholder="0"
                                                step="0.01"
                                            />
                                        ) : (
                                            <span className="shopFee-fee-value">{shop.commission || '0'}%</span>
                                        )}
                                    </td>
                                    <td className="shopFee-table-cell">
                                        <div className="shopFee-actions">
                                            {editingShop === shop.id ? (
                                                <>
                                                    <button
                                                        className="shopFee-icon-btn shopFee-btn-success"
                                                        onClick={handleSave}
                                                        disabled={loading}
                                                    >
                                                        <Save size={16} />
                                                    </button>
                                                    <button
                                                        className="shopFee-icon-btn shopFee-btn-secondary"
                                                        onClick={handleCancel}
                                                        disabled={loading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        className="shopFee-icon-btn shopFee-btn-primary"
                                                        onClick={() => handleEdit(shop)}
                                                        disabled={loading}
                                                        title="Edit Fees"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        className="shopFee-icon-btn shopFee-btn-danger"
                                                        onClick={() => resetShopFees(shop.id)}
                                                        disabled={loading}
                                                        title="Reset to default"
                                                    >
                                                        <RotateCw size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ShopFeesManager;